/**
 * CORPS CONFIGURATION DIALOG
 * Phase 2 UI Component - Step 1
 * 
 * This component allows editing Corps-level configuration:
 * - Personnel count
 * - Operational state
 * - Position from FEBA
 * - Operational phases (with cascade to divisions/brigades)
 * 
 * INTEGRATION: Add this component to Tab2_Operations.jsx
 * 
 * Date: January 30, 2026
 * For: Dr. Tatar Radu MD - PSP/CRE Tool v3.0
 *
 * Version: 1.7.0 (April 8, 2026) - V3.1-CORPS-MISSIONS: posture-linked mission dropdown.
 *   Mission Select now filters to doctrinally valid options for the selected posture
 *   (mirrors Tab2_Operations.jsx POSTURE_MISSIONS map). POSTURE_MISSIONS + ALL_MISSIONS
 *   constants added at module level. Auto-correction fires via setTimeout when a saved
 *   mission is no longer valid after posture change. Removed emoji violations:
 *   '🏙️ Urban Operations' → 'Urban Operations', '🌊 River Crossing' → 'River Crossing'.
 *
 * Version: 1.6.0 (March 25, 2026) - BUG-193: Save button label wrong at BRIGADE echelon.
 *   Root cause: subordinateLabel ternary only handled 'Division' and fallback 'Divisions'.
 *   At BRIGADE echelon echelonLabel='Brigade' → fell through to 'Divisions' →
 *   button read "Save & Cascade to Divisions". No subordinates exist at brigade level.
 *   Fix: echelonLabel === 'Brigade' → subordinateLabel = null → button reads "Save & Apply".
 *
 * Version: 1.5.0 (March 19, 2026) - BUG-165: Save button label wrong in DIVISION echelon.
 *   "Save & Cascade to Divisions" is correct for CORPS echelon but wrong when echelonLabel
 *   is "Division" (planning at division level — subordinates are brigades, not divisions).
 *   Fix: derive subordinateLabel from echelonLabel ('Division' → 'Brigades', else 'Divisions').
 *   Button now reads "Save & Cascade to Brigades" in DIVISION echelon.
 * Version: 1.4.0 (March 18, 2026) - APP_RULES: remaining bare emoji (📋 missed in v1.3.0).
 *   📋 → DATA_ICONS.CLIPBOARD. DATA_ICONS import added.
 * Version: 1.3.0 (March 18, 2026) - BUG-149 + APP_RULES bare emoji:
 *   BUG-149: DialogTitle hardcoded "Corps Configuration" regardless of echelon.
 *     New optional prop `echelonLabel` (default "Corps"). Tab2 passes
 *     echelonLabel so planning at DIVISION level shows "Division Configuration".
 *   APP_RULES: All bare emoji replaced with icons.js named constants.
 *     🎖️ → MILITARY_ICONS.CORPS  |  📚 → STATUS_ICONS.INFO
 *     ✅ → STATUS_ICONS.GREEN_CHECK  |  ⚠️ → STATUS_ICONS.YELLOW_WARNING
 *     ❌ → STATUS_ICONS.RED_X
 * Version: 1.2.0 (March 13, 2026) - BUG-090: Grid v1 → v2 migration in Corps HQ Configuration block.
 *   Three <Grid item xs={12} md={4}> → <Grid size={{ xs: 12, md: 4 }}> (lines 230, 245, 260).
 *   Source of MUI Grid v1 deprecation warnings incorrectly attributed to Tab4.
 * Version: 1.1.0 (March 13, 2026) - BUG-076: Posture/mission auto-sync in phase editing.
 *   updatePhase() now auto-sets mission_type when operational_posture changes, using the
 *   same postureToMissionType mapping as Tab2_Operations getMissionFromPosture(). User
 *   retains the ability to manually override mission_type independently after posture is set.
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Slider,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Alert,
  AlertTitle,
  Stack,
  Box,
  Grid,
  InputAdornment,
  Divider,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import {
  MILITARY_ICONS,
  STATUS_ICONS,
  DATA_ICONS,
} from '../utils/icons.js';
import PhaseCasualtyControls from './PhaseCasualtyControls';

// V3.1-CORPS-MISSIONS: posture-linked mission dropdown.
// Maps each corps operational posture to the set of doctrinally valid missions.
// Mirrors the POSTURE_MISSIONS map in Tab2_Operations.jsx.
const POSTURE_MISSIONS = {
  DELIBERATE_ATTACK:    [{ value: 'ATTACK', label: 'Attack' }, { value: 'URBAN', label: 'Urban Operations' }, { value: 'RIVER_CROSSING', label: 'River Crossing' }],
  HASTY_ATTACK:         [{ value: 'ATTACK', label: 'Attack' }, { value: 'URBAN', label: 'Urban Operations' }, { value: 'RIVER_CROSSING', label: 'River Crossing' }],
  EXPLOITATION:         [{ value: 'EXPLOITATION', label: 'Exploitation' }, { value: 'ATTACK', label: 'Attack' }],
  MOVEMENT_TO_CONTACT:  [{ value: 'ATTACK', label: 'Attack' }],
  PURSUIT:              [{ value: 'ATTACK', label: 'Attack' }, { value: 'EXPLOITATION', label: 'Exploitation' }],
  MOBILE_DEFENSE:       [{ value: 'DEFEND', label: 'Defend' }],
  DELAY:                [{ value: 'DEFEND', label: 'Defend' }],
  WITHDRAWAL:           [{ value: 'CONSOLIDATION', label: 'Consolidation' }],
  RETIREMENT:           [{ value: 'CONSOLIDATION', label: 'Consolidation' }],
  DELIBERATE_DEFENSE:   [{ value: 'DEFEND', label: 'Defend' }],
  HASTY_DEFENSE:        [{ value: 'DEFEND', label: 'Defend' }],
  CONSOLIDATION:        [{ value: 'CONSOLIDATION', label: 'Consolidation' }],
  RESERVE:              [{ value: 'RESERVE', label: 'Reserve' }],
  MARCH_TACTICAL:       [{ value: 'MOVEMENT', label: 'Movement' }],
  MARCH_ADMINISTRATIVE: [{ value: 'MOVEMENT', label: 'Movement' }],
  TRANSIT:              [{ value: 'MOVEMENT', label: 'Movement' }],
};

// Fallback: all missions, used when posture is unknown or not yet set
const ALL_MISSIONS = [
  { value: 'ATTACK',         label: 'Attack' },
  { value: 'DEFEND',         label: 'Defend' },
  { value: 'URBAN',          label: 'Urban Operations' },
  { value: 'RIVER_CROSSING', label: 'River Crossing' },
  { value: 'EXPLOITATION',   label: 'Exploitation' },
  { value: 'CONSOLIDATION',  label: 'Consolidation' },
  { value: 'RESERVE',        label: 'Reserve' },
  { value: 'MOVEMENT',       label: 'Movement' },
];

/**
 * Corps Configuration Dialog Component
 * 
 * @param {boolean} open - Dialog open state
 * @param {Function} onClose - Close handler
 * @param {Function} onSave - Save handler (receives complete config object)
 * @param {Object} initialConfig - Current Corps configuration
 * @param {number} operationDurationDays - Total operation duration for validation
 * @param {Array} divisions - Array of division configs to show inheritance status
 */
const CorpsConfigDialog = ({
  open,
  onClose,
  onSave,
  initialConfig = {
    personnel: 2000,
    operational_state: 'SUPPORT_ACTIVE',
    position_feba_km: 60,
    phases: []
  },
  operationDurationDays = 10,
  divisions = [],
  echelonLabel = 'Corps',  // BUG-149: passed by Tab2 — "Division" when planning at division level
}) => {
  // BUG-165 FIX: subordinate echelon label for Save button.
  // CORPS echelon → subordinates are Divisions. DIVISION echelon → subordinates are Brigades.
  // BUG-193 FIX (March 25, 2026): BRIGADE echelon fell through to 'Divisions' (wrong).
  // At BRIGADE echelon this dialog configures Brigade HQ & Phases — no subordinates
  // to cascade to. subordinateLabel=null → button reads "Save & Apply".
  const subordinateLabel = echelonLabel === 'Division' ? 'Brigades'
    : echelonLabel === 'Brigade' ? null
    : 'Divisions';
  
  // Local state for editing
  const [personnel, setPersonnel] = useState(initialConfig.personnel || 2000);
  const [operationalState, setOperationalState] = useState(initialConfig.operational_state || 'SUPPORT_ACTIVE');
  const [positionFeba, setPositionFeba] = useState(initialConfig.position_feba_km || 60);
  const [phases, setPhases] = useState(initialConfig.phases || []);
  
  // Reset state when dialog opens with new config
  useEffect(() => {
    if (open) {
      setPersonnel(initialConfig.personnel || 2000);
      setOperationalState(initialConfig.operational_state || 'SUPPORT_ACTIVE');
      setPositionFeba(initialConfig.position_feba_km || 60);
      setPhases(initialConfig.phases || []);
    }
  }, [open, initialConfig]);
  
  // Calculate phase days from durations
  const calculatePhaseDays = (phasesArray) => {
    let currentDay = 1;
    return phasesArray.map(phase => {
      const startDay = currentDay;
      const endDay = currentDay + phase.duration_days - 1;
      currentDay = endDay + 1;
      return {
        ...phase,
        start_day: startDay,
        end_day: endDay
      };
    });
  };
  
  // BUG-076 FIX (March 13, 2026): posture → mission auto-sync.
  // When operational_posture changes, mission_type is automatically updated to the
  // doctrinally expected value. User can still manually override mission_type afterwards.
  const POSTURE_TO_MISSION = {
    ATTACK:                'ATTACK',
    HASTY_ATTACK:          'ATTACK',
    DELIBERATE_ATTACK:     'ATTACK',
    EXPLOITATION:          'EXPLOITATION',
    PURSUIT:               'ATTACK',
    MOVEMENT_TO_CONTACT:   'ATTACK',
    MOBILE_DEFENSE:        'DEFEND',
    RETIREMENT:            'CONSOLIDATION',
    DEFENSE:               'DEFEND',
    HASTY_DEFENSE:         'DEFEND',
    DELIBERATE_DEFENSE:    'DEFEND',
    DELAY:                 'DEFEND',
    WITHDRAWAL:            'CONSOLIDATION',
    RETIRE:                'CONSOLIDATION',
    RESERVE:               'RESERVE',
    MARCH:                 'MOVEMENT',
    MARCH_TACTICAL:        'MOVEMENT',
    MARCH_ADMINISTRATIVE:  'MOVEMENT',
    MARCHING:              'MOVEMENT',
    TRANSIT:               'MOVEMENT',
    RECONSTITUTION:        'SUPPORTING',
    SUPPORT_OPERATIONS:    'SUPPORTING',
    CONSOLIDATION:         'CONSOLIDATION'
  };

  // Update phase field
  const updatePhase = (phaseIndex, field, value) => {
    const newPhases = [...phases];
    const updatedPhase = { ...newPhases[phaseIndex], [field]: value };
    // Auto-sync mission_type when posture changes
    if (field === 'operational_posture') {
      updatedPhase.mission_type = POSTURE_TO_MISSION[value] || 'DEFEND';
    }
    newPhases[phaseIndex] = updatedPhase;
    setPhases(calculatePhaseDays(newPhases));
  };
  
  // Add new phase
  const addPhase = () => {
    const newPhase = {
      phase_number: phases.length + 1,
      duration_days: 1,
      operational_posture: 'DELIBERATE_DEFENSE',
      mission_type: 'DEFEND',
      description: `Phase ${phases.length + 1}`
    };
    setPhases(calculatePhaseDays([...phases, newPhase]));
  };
  
  // Delete phase
  const deletePhase = (phaseIndex) => {
    const newPhases = phases.filter((_, i) => i !== phaseIndex);
    const renumbered = newPhases.map((p, i) => ({
      ...p,
      phase_number: i + 1
    }));
    setPhases(calculatePhaseDays(renumbered));
  };
  
  // Validation
  const totalPhaseDays = phases.reduce((sum, p) => sum + p.duration_days, 0);
  const phaseDaysValid = totalPhaseDays === operationDurationDays;
  const phaseDaysError = totalPhaseDays > operationDurationDays;
  const phaseDaysWarning = totalPhaseDays < operationDurationDays && phases.length > 0;
  
  // Save handler
  const handleSave = () => {
    onSave({
      personnel,
      operational_state: operationalState,
      position_feba_km: positionFeba,
      phases
    });
  };
  
  // Count divisions inheriting
  const inheritingDivisions = divisions.filter(d => d.uses_corps_phases);
  
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="h5">
            {MILITARY_ICONS.CORPS} {echelonLabel} Configuration
          </Typography>
          <Chip 
            label={`${phases.length} Phases`} 
            color="primary" 
            size="small"
          />
        </Stack>
      </DialogTitle>
      
      <DialogContent>
        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>{STATUS_ICONS.INFO} Military Doctrine</AlertTitle>
          {/* R2-17 (2026-10-20): the text names the echelon being planned; the cascade sentence only where there are subordinates to cascade to */}
          The {echelonLabel.toLowerCase()} commander sets the operational phases for the entire operation.
          {divisions.length > 0 && (
            <> Changes cascade to {inheritingDivisions.length} of {divisions.length} {subordinateLabel ? subordinateLabel.toLowerCase() : 'subordinate units'} (those with custom phases are not affected).</>
          )}
        </Alert>
        
        {/* Corps HQ Configuration: only when planning at corps level. R2-17 (2026-10-20): at brigade and division level it showed the Corps HQ (wrong unit);
            the HQ of the planned echelon is set in Tab 1. The three values are saved but the calculation does not read them. */}
        {echelonLabel === 'Corps' && (
          <>
        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          Corps HQ Configuration
        </Typography>
        
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label="Corps HQ Personnel"
              type="number"
              value={personnel}
              InputProps={{
                readOnly: true,
                endAdornment: <InputAdornment position="end">troops</InputAdornment>
              }}
              helperText="Configured in Tab 1 — modify there"
              sx={{ '& .MuiInputBase-input': { color: 'text.secondary' } }}
            />
          </Grid>
          
          <Grid size={{ xs: 12, md: 4 }}>
            <FormControl fullWidth>
              <InputLabel>Operational State</InputLabel>
              <Select
                value={operationalState}
                onChange={(e) => setOperationalState(e.target.value)}
                label="Operational State"
              >
                <MenuItem value="SUPPORT_ACTIVE">Support Active</MenuItem>
                <MenuItem value="TRUE_RESERVE">True Reserve</MenuItem>
                <MenuItem value="COMBAT_ENGAGED">Combat Engaged</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid size={{ xs: 12, md: 4 }}>
            <Typography variant="body2" gutterBottom>
              Position from FEBA: {positionFeba} km
            </Typography>
            <Slider
              value={positionFeba}
              onChange={(e, newValue) => setPositionFeba(newValue)}
              min={30}
              max={100}
              step={5}
              marks={[
                { value: 30, label: '30km' },
                { value: 60, label: '60km' },
                { value: 100, label: '100km' }
              ]}
              valueLabelDisplay="auto"
            />
            <Typography variant="caption" color="text.secondary">
              Corps HQ typically 50-80km behind FEBA
            </Typography>
          </Grid>
        </Grid>
        
          </>
        )}
        
        {echelonLabel === 'Corps' && <Divider sx={{ my: 3 }} />}
        
        {/* Operational Phases */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6">
            Operational Phases
          </Typography>
          <Button
            startIcon={<AddIcon />}
            onClick={addPhase}
            variant="outlined"
            size="small"
          >
            Add Phase
          </Button>
        </Stack>
        
        {phases.length === 0 ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No phases configured. Add phases to define the operation timeline.
            Click "Add Phase" to begin.
          </Alert>
        ) : (
          <>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Phase</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Days</TableCell>
                    <TableCell>Posture</TableCell>
                    <TableCell>Mission</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {phases.map((phase, index) => (
                    <React.Fragment key={index}>
                    <TableRow>
                      <TableCell>
                        <Chip 
                          label={`Phase ${phase.phase_number}`} 
                          size="small" 
                          color="primary" 
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          size="small"
                          value={phase.duration_days}
                          onChange={(e) => updatePhase(index, 'duration_days', Number(e.target.value))}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">days</InputAdornment>
                          }}
                          sx={{ width: 100 }}
                          inputProps={{ min: 1 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {phase.start_day}-{phase.end_day}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <FormControl size="small" sx={{ minWidth: 150 }}>
                          <Select
                            value={phase.operational_posture}
                            onChange={(e) => updatePhase(index, 'operational_posture', e.target.value)}
                          >
                            <MenuItem value="DELIBERATE_ATTACK">Deliberate Attack</MenuItem>
                            <MenuItem value="HASTY_ATTACK">Hasty Attack</MenuItem>
                            <MenuItem value="EXPLOITATION">Exploitation</MenuItem>
                            <MenuItem value="DELIBERATE_DEFENSE">Deliberate Defense</MenuItem>
                            <MenuItem value="HASTY_DEFENSE">Hasty Defense</MenuItem>
                            <MenuItem value="MOVEMENT_TO_CONTACT">Movement to Contact</MenuItem>
                            <MenuItem value="PURSUIT">Pursuit</MenuItem>
                            <MenuItem value="MOBILE_DEFENSE">Mobile Defense</MenuItem>
                            <MenuItem value="DELAY">Delay</MenuItem>
                            <MenuItem value="WITHDRAWAL">Withdrawal</MenuItem>
                            <MenuItem value="RETIREMENT">Retirement (out of contact)</MenuItem>
                            <MenuItem value="CONSOLIDATION">Consolidation</MenuItem>
                            <MenuItem value="RESERVE">Reserve</MenuItem>
                            <MenuItem value="MARCH_TACTICAL">
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                Tactical March
                                <Tooltip 
                                  title="Combat-ready movement. High security, slow speed. Can engage enemy immediately. Use when enemy contact possible. Battle rate: 0.1%/day"
                                  arrow
                                  placement="right"
                                >
                                  <InfoIcon fontSize="small" color="action" />
                                </Tooltip>
                              </Box>
                            </MenuItem>
                            <MenuItem value="MARCH_ADMINISTRATIVE">
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                Administrative March
                                <Tooltip 
                                  title="Fast movement in safe rear areas. Low combat readiness. Needs 30-60 min to reorganize for combat. Battle rate: 0.01%/day"
                                  arrow
                                  placement="right"
                                >
                                  <InfoIcon fontSize="small" color="action" />
                                </Tooltip>
                              </Box>
                            </MenuItem>
                            <MenuItem value="TRANSIT">
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                Transit
                                <Tooltip 
                                  title="Strategic theater-to-theater movement. Minimal combat readiness. Very secure areas only. Battle rate: 0.005%/day"
                                  arrow
                                  placement="right"
                                >
                                  <InfoIcon fontSize="small" color="action" />
                                </Tooltip>
                              </Box>
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          // V3.1-CORPS-MISSIONS: filter missions to those valid for current posture
                          const allowedMissions = POSTURE_MISSIONS[phase.operational_posture] || ALL_MISSIONS;
                          // Auto-correct if saved mission is no longer valid for the selected posture
                          if (phase.mission_type && !allowedMissions.find(m => m.value === phase.mission_type)) {
                            setTimeout(() => updatePhase(index, 'mission_type', allowedMissions[0].value), 0);
                          }
                          const safeValue = allowedMissions.find(m => m.value === phase.mission_type)
                            ? phase.mission_type
                            : allowedMissions[0].value;
                          return (
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                              <Select
                                value={safeValue}
                                onChange={(e) => updatePhase(index, 'mission_type', e.target.value)}
                              >
                                {allowedMissions.map((m) => (
                                  m.value === 'MOVEMENT' ? (
                                    <MenuItem key="MOVEMENT" value="MOVEMENT">
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                        Movement
                                        <Tooltip
                                          title="All march postures map to MOVEMENT mission. Medical facilities offline during movement. Casualties evacuated to rear area facilities."
                                          arrow
                                          placement="right"
                                        >
                                          <InfoIcon fontSize="small" color="action" />
                                        </Tooltip>
                                      </Box>
                                    </MenuItem>
                                  ) : (
                                    <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                                  )
                                ))}
                              </Select>
                            </FormControl>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={phase.description || ''}
                          onChange={(e) => updatePhase(index, 'description', e.target.value)}
                          placeholder="Phase description"
                          sx={{ minWidth: 200 }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => deletePhase(index)}
                          disabled={phases.length === 1}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    {/* 2026-10-13: casualty profile row (resistance, enemy ratio, maneuver group, special condition) */}
                    <TableRow>
                      <TableCell colSpan={7} sx={{ pt: 0.5, pb: 0.75 }}>
                        <PhaseCasualtyControls
                          phase={phase}
                          onChange={(field, value) => updatePhase(index, field, value)}
                        />
                      </TableCell>
                    </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* Validation */}
            {phaseDaysValid && (
              <Alert severity="success" icon={<CheckCircleIcon />}>
                {STATUS_ICONS.GREEN_CHECK} All {operationDurationDays} days covered by phases.
              </Alert>
            )}
            {phaseDaysWarning && (
              <Alert severity="warning" icon={<WarningIcon />}>
                {STATUS_ICONS.YELLOW_WARNING} Phases cover {totalPhaseDays} days, but operation is {operationDurationDays} days. 
                Add {operationDurationDays - totalPhaseDays} more day(s).
              </Alert>
            )}
            {phaseDaysError && (
              <Alert severity="error" icon={<WarningIcon />}>
                {STATUS_ICONS.RED_X} Phases total {totalPhaseDays} days, exceeding operation duration ({operationDurationDays} days). 
                Reduce by {totalPhaseDays - operationDurationDays} day(s).
              </Alert>
            )}
          </>
        )}
        
        {/* Inheritance Status */}
        {divisions.length > 0 && (
          <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              {DATA_ICONS.CLIPBOARD} Divisions Inheriting Corps Phases
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {divisions.map(div => (
                <Chip
                  key={div.division_id}
                  label={`Division ${div.division_number}`}
                  color={div.uses_corps_phases ? 'success' : 'default'}
                  size="small"
                  icon={div.uses_corps_phases ? <CheckCircleIcon /> : <InfoIcon />}
                />
              ))}
            </Stack>
            {inheritingDivisions.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {inheritingDivisions.length} of {divisions.length} division(s) will receive these phases
              </Typography>
            )}
            {divisions.filter(d => !d.uses_corps_phases).length > 0 && (
              <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
                {divisions.filter(d => !d.uses_corps_phases).length} division(s) have custom phases (not affected)
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained"
          disabled={!phaseDaysValid && phases.length > 0}
        >
          {subordinateLabel ? `Save & Cascade to ${subordinateLabel}` : 'Save & Apply'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CorpsConfigDialog;

/**
 * INTEGRATION INSTRUCTIONS FOR Tab2_Operations.jsx:
 * 
 * 1. Import this component at the top:
 *    import CorpsConfigDialog from './CorpsConfigDialog';
 * 
 * 2. Add the component before the closing return statement:
 *    <CorpsConfigDialog
 *      open={editingCorps}
 *      onClose={handleCloseCorpsDialog}
 *      onSave={handleSaveCorpsConfig}
 *      initialConfig={{
 *        personnel: corpsPersonnel,
 *        operational_state: corpsOperationalState,
 *        position_feba_km: corpsPositionFeba,
 *        phases: corpsPhases
 *      }}
 *      operationDurationDays={totalDuration}
 *      divisions={divisionConfigs}
 *    />
 * 
 * 3. Add a button to open the dialog in your Corps Config section:
 *    <Button 
 *      variant="outlined"
 *      startIcon={<SettingsIcon />}
 *      onClick={handleEditCorpsConfig}
 *    >
 *      Configure Corps HQ & Phases
 *    </Button>
 * 
 * 4. The handlers (handleEditCorpsConfig, handleCloseCorpsDialog, handleSaveCorpsConfig)
 *    already exist in v6.1 - no additional code needed!
 */
